import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Dialog as Radix } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Modal dialog on Radix (focus trap, Escape, aria) with a soft scale-and-lift entrance over a
// blurred backdrop.

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Dialog({ open, onOpenChange, title, description, children, footer }: DialogProps) {
  const { t } = useTranslation();
  return (
    <Radix.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Radix.Portal forceMount>
            <Radix.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </Radix.Overlay>
            <Radix.Content
              asChild
              forceMount
              aria-describedby={description ? undefined : undefined}
            >
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-surface p-6 elevated"
                initial={{ opacity: 0, scale: 0.94, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              >
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div>
                    <Radix.Title className="font-display text-xl font-bold text-text">
                      {title}
                    </Radix.Title>
                    {description && (
                      <Radix.Description className="mt-1 text-base text-muted">
                        {description}
                      </Radix.Description>
                    )}
                  </div>
                  <Radix.Close
                    className="rounded-sm p-1.5 text-muted transition-colors hover:bg-subtle hover:text-text"
                    aria-label={t('common.close')}
                  >
                    <X size={18} />
                  </Radix.Close>
                </div>
                {children}
                {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
              </motion.div>
            </Radix.Content>
          </Radix.Portal>
        )}
      </AnimatePresence>
    </Radix.Root>
  );
}
